/**
 * R18 Phase 1 follow-up — Audience join card.
 *
 * Lightweight pre-room card for users whose JWT has can_publish=false in
 * Lounge/Club rooms. We deliberately do NOT call getUserMedia here — these
 * users are joining as listeners and shouldn't be forced through a
 * camera-permission prompt at room entry.
 *
 * If the host later approves their Request Camera, the camera_decision
 * data-message handler in Room.jsx flips canPublish=true and calls
 * setCameraEnabled(true) — THAT call prompts for camera permission at the
 * moment it's actually needed.
 *
 * Testids:
 *   - audience-join-preview      (root)
 *   - audience-join-enter-btn
 *   - audience-join-name-input
 */
import React, { useState } from "react";
import { Headphones, ArrowRight, Camera, Loader2 } from "lucide-react";

export default function AudienceJoinCard({
  roomName,
  roomMode = "club",
  initialDisplayName = "",
  djName,
  onEnter,
}) {
  const [displayName, setDisplayName] = useState(initialDisplayName || "");
  const [entering, setEntering] = useState(false);

  const enter = () => {
    if (entering || !displayName.trim()) return;
    setEntering(true);
    onEnter?.({ displayName: displayName.trim() });
  };

  const modeLabel = roomMode === "lounge" ? "Listening space" : "DJ set";
  return (
    <div
      className="min-h-screen w-full bg-[#050508] text-white grid place-items-center p-4 sm:p-6"
      data-testid="audience-join-preview"
    >
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#0a0a14]/85 backdrop-blur shadow-2xl overflow-hidden">
        <header className="px-6 py-5 border-b border-white/8 text-center">
          <div className="w-12 h-12 mx-auto rounded-xl bg-gradient-to-br from-[#FF007F] to-[#00F0FF] grid place-items-center mb-3">
            <Headphones className="w-5 h-5 text-black" />
          </div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-white/45 mb-1">
            Joining {modeLabel}
          </div>
          <h1 className="text-lg font-bold truncate">{roomName || "Full Spectrum Center"}</h1>
          {djName && (
            <div className="text-[11px] text-white/55 mt-1">DJ: {djName}</div>
          )}
        </header>

        <div className="p-5 space-y-4">
          <p className="text-sm text-white/75 leading-relaxed">
            You&apos;re joining as a <strong className="text-white">listener</strong> — you&apos;ll hear the DJ and see the crowd, with your camera off.
            You can tap <strong className="text-white">Request Camera</strong> inside the room any time to ask the host for a spot on the wall.
          </p>

          <label className="block">
            <div className="text-[10px] uppercase tracking-[0.18em] text-white/45 mb-1.5">Display name</div>
            <input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="What should we call you?"
              maxLength={40}
              autoFocus
              className="w-full bg-black/40 border border-white/12 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#00F0FF]/60"
              data-testid="audience-join-name-input"
            />
          </label>

          <div className="flex items-center gap-2 text-[11px] text-white/45 rounded-md border border-white/8 bg-white/[0.03] px-3 py-2">
            <Camera className="w-3.5 h-3.5 shrink-0" />
            <span>No camera or microphone access requested. Audio-only listening.</span>
          </div>

          <button
            disabled={entering || !displayName.trim()}
            onClick={enter}
            className="w-full bg-gradient-to-r from-[#FF007F] to-[#00F0FF] text-[#0A0A0F] font-bold uppercase tracking-[0.14em] text-xs py-3 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition"
            data-testid="audience-join-enter-btn"
          >
            {entering ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
            Enter Room <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
